/**
 * Gợi ý "Giá trị còn lại" của gói nhập từ sổ tay cũ (STT 2.3, ADR-090).
 *
 * Giá trị này là CƠ SỞ HOA HỒNG của từng buổi gói cũ (ADR-039/045) nên để trống / 0 là thợ mất hoa hồng.
 * Form điền sẵn một con số gần đúng, người nhập sửa lại theo số tiền khách thật đã trả. Máy chủ KHÔNG
 * tự điền (giấu sai số khỏi người nhập) — chỉ kiểm 0đ phải đi kèm cờ "Gói tặng".
 *
 * Hàm thuần, an toàn trình duyệt, chỉ BigInt (CLAUDE.md §1).
 */
import { DEFAULT_ROUND_STEP, roundDownTo, type Vnd } from './money.ts'

/** Một gói mẫu đang bán cho đúng dịch vụ này: giá cả gói và TỔNG buổi (mua + tặng) lúc bán. */
export interface GoiMauGoiY {
  priceVnd: Vnd
  tongBuoi: number
}

export interface GoiYSoCu {
  giaTriVnd: Vnd
  /** `goi-mau`: theo giá gói mẫu của tiệm · `gia-le`: số buổi × giá lẻ · `khong`: không gợi ý được. */
  nguon: 'goi-mau' | 'gia-le' | 'khong'
}

/**
 * Gợi ý giá trị còn lại của `soBuoi` buổi:
 *  1. Có gói mẫu cùng dịch vụ → giá gói ÷ tổng buổi × số buổi còn (làm tròn xuống 1.000đ). Nhiều gói mẫu thì
 *     lấy gói có giá mỗi buổi THẤP nhất — gợi ý thấp hơn thì hoa hồng không bị thổi phồng.
 *  2. Không có (hoặc ra 0đ) → số buổi × giá lẻ hiện tại (làm tròn xuống 1.000đ).
 *  3. Giá lẻ 0 → không gợi ý.
 */
export function goiYGiaTriSoCu(
  soBuoi: number,
  giaLeVnd: Vnd,
  goiMau: readonly GoiMauGoiY[] = [],
): GoiYSoCu {
  if (!Number.isInteger(soBuoi) || soBuoi < 1) return { giaTriVnd: 0n, nguon: 'khong' }
  const n = BigInt(soBuoi)

  let tot: GoiMauGoiY | null = null
  for (const g of goiMau) {
    if (g.priceVnd <= 0n || !Number.isInteger(g.tongBuoi) || g.tongBuoi < 1) continue
    // Giá mỗi buổi của g < của tot ⇔ g.price × tot.tong < tot.price × g.tong (so bằng nhân chéo, không chia).
    if (tot === null || g.priceVnd * BigInt(tot.tongBuoi) < tot.priceVnd * BigInt(g.tongBuoi)) {
      tot = g
    }
  }
  if (tot !== null) {
    const theoGoi = roundDownTo((tot.priceVnd * n) / BigInt(tot.tongBuoi), DEFAULT_ROUND_STEP)
    if (theoGoi > 0n) return { giaTriVnd: theoGoi, nguon: 'goi-mau' }
  }

  if (giaLeVnd <= 0n) return { giaTriVnd: 0n, nguon: 'khong' }
  const theoLe = roundDownTo(n * giaLeVnd, DEFAULT_ROUND_STEP)
  return theoLe > 0n ? { giaTriVnd: theoLe, nguon: 'gia-le' } : { giaTriVnd: 0n, nguon: 'khong' }
}

/**
 * Lỗi của "Giá trị còn lại" gói sổ cũ — MỘT luật cho máy chủ (route + repo) và form web (V1 + N1, ADR-090):
 *   - `tang-co-gia-tri`: đánh dấu Gói tặng mà giá trị > 0 (che một gói khách đã trả tiền);
 *   - `thieu-gia-tri`: không tặng mà giá trị ≤ 0 (thợ 0đ hoa hồng mọi buổi, không ai biết);
 *   - `qua-nho`: không tặng, giá trị > 0 nhưng < số buổi × 1.000đ. Giá trị buổi làm tròn xuống 1.000đ nên phần
 *     còn lại bị dồn hết vào buổi đầu (9.000đ / 10 buổi: buổi 1 = 9.000đ, 9 buổi sau 0đ → thợ mất hoa hồng 9 buổi).
 * `null` = hợp lệ. `soBuoiCon` phải là số nguyên ≥ 1 (người gọi đã kiểm ở biên) — sai là lỗi lập trình.
 */
export type LoiGiaTriSoCu = 'tang-co-gia-tri' | 'thieu-gia-tri' | 'qua-nho'

/** Giá trị còn lại tối thiểu của gói sổ cũ có `soBuoiCon` buổi: mỗi buổi ít nhất một bước làm tròn (1.000đ). */
export function giaTriToiThieuSoCu(soBuoiCon: number): Vnd {
  if (!Number.isInteger(soBuoiCon) || soBuoiCon < 1) {
    throw new Error(`So buoi con phai la so nguyen >= 1, nhan: ${soBuoiCon}`)
  }
  return BigInt(soBuoiCon) * DEFAULT_ROUND_STEP
}

export function loiGiaTriConLaiSoCu(
  giaTriConVnd: Vnd,
  soBuoiCon: number,
  tang: boolean,
): LoiGiaTriSoCu | null {
  const toiThieu = giaTriToiThieuSoCu(soBuoiCon)
  if (tang) return giaTriConVnd === 0n ? null : 'tang-co-gia-tri'
  if (giaTriConVnd <= 0n) return 'thieu-gia-tri'
  return giaTriConVnd < toiThieu ? 'qua-nho' : null
}
